import { Component, Element, h, Prop } from "@stencil/core"
import {
  getIconLinkClassNames,
  hasSlot,
} from "../../core/helpers/webcomponent-helpers"
import { NotificationType } from "../../core/types/types"
import { IconName } from "../../core/types/icon-names"
import { getAppearance } from "../../core/helpers/webcomponent-helpers"

/**
 * @slot first - Elements which appears on the left half of the component.
 * @slot interaction - Elements like buttons which will be positioned at the bottom.
 */
@Component({
  tag: "sdx-card",
  styleUrl: "card.scss",
  shadow: true,
})
export class Card {
  private iconMap = {
    confirm: "icon-check-mark",
    confirmation: "icon-check-mark",
    warning: "icon-exclamation-mark",
    alert: "icon-exclamation-mark",
    general: "icon-information",
  }

  @Element() public el!: HTMLSdxCardElement

  /**
   * The layout type defines the appearance types of the card.
   */
  @Prop() public layout:
    | "default"
    | "interaction"
    | "notification"
    | "inline-notification"
    | "split" = "default"

  /**
   * Background colors. Use "dark" when placed on a dark background.
   */
  @Prop() public background?: "grey" | "orchid" | "blue" | "dark"

  /**
   * Title that will be displayed in the card’s header.
   */
  @Prop() public label?: string

  /**
   * What aria level the label in the header relates to for accessibility.
   */
  @Prop() public labelAriaLevel?: number

  /**
   * The icon which is displayed next to the label.
   */
  @Prop() public iconName?: IconName

  /**
   * icons used for in-line notifications should be filled by default.
   * @private
   */
  @Prop() public filledIcon?: boolean = true

  /**
   * What image to display (also supports SVG).
   */
  @Prop() public imageSrc?: string = ""

  /**
   * How to handle the loading of the image if outside the visual viewport (reflects the `loading` property, see
   * [mdn web docs](//developer.mozilla.org/en-US/docs/Web/API/HTMLImageElement/loading)).
   */
  @Prop() public imageLoading: "lazy" | "eager" = "lazy"

  /**
   * How to prioritize the fetch of the image relative to others (reflects the `fetchPriority` property, see
   * [mdn web docs](//developer.mozilla.org/en-US/docs/Web/API/HTMLImageElement/fetchPriority)).
   */
  @Prop() public imageFetchPriority: "high" | "low" | "auto" = "auto"

  /**
   * An alternate text for the image if it can not be displayed and for screen
   * readers.
   */
  @Prop() public imageAlt?: string = ""

  /**
   * How the image fits inside (reflects the CSS `"object-fit"` property, see
   * [mdn web docs](//developer.mozilla.org/en-US/docs/Web/CSS/object-fit)).
   */
  @Prop() public objectFit?: string

  /**
   * Gives the card a suitable colour. `confirm` and `alert` are deprecated.
   */
  @Prop() public notificationType: NotificationType | "confirm" = "general"

  /**
   * Renders a standalone link at the bottom of the card. Needs the
   * `href-label` attribute. The attribute `layout="interaction"` makes the
   * whole card clickable.
   */
  @Prop() public href?: string

  /**
   * Renders a standalone link at the bottom of the card. Needs the `href`
   * attribute. The attributes `layout="interaction"` makes the whole card
   * clickable.
   */
  @Prop() public hrefLabel?: string

  /**
   * Add a meaningful description to the link for screen readers.
   */
  @Prop() public hrefAriaLabel?: string

  /**
   * Standalone link "target" (`"_blank"`, `"_parent"`, `"_top"`, `"_self"`, or
   * the name of a window or frame). Needs the `href` attribute.
   */
  @Prop() public target?: string

  /**
   * The height of the interaction card. `"small"` for no minimal height on desktop.
   */
  @Prop() public size?: "small" | undefined

  /**
   * Callback when the link is clicked.
   * @private
   */
  @Prop() public linkClickCallback: (e: MouseEvent) => void = () => undefined

  /**
   * Disable animations for testing.
   * @private
   */
  @Prop() public animated: boolean = true

  private hasInteractionArea(): boolean {
    return hasSlot(this.el, "interaction") || (!!this.href && !!this.hrefLabel)
  }

  private isNotificationishCard(): boolean {
    const layout = this.layout

    return layout === "notification" || layout === "inline-notification"
  }

  private getLabel() {
    return this.layout === "interaction"
      ? this.label || this.hrefLabel
      : this.label
  }

  private getDescription() {
    return this.layout === "interaction" && this.label ? this.hrefLabel : ""
  }

  private getIconName(): IconName | undefined {
    // The icon can be overwritten, but if none is set, fall back to the
    // associated default
    if (this.iconName) {
      return this.iconName
    }

    if (!this.isNotificationishCard()) {
      return this.iconName
    }

    const iconType = this.iconMap[this.notificationType] || this.iconMap.general
    const iconSuffix = this.filledIcon ? "-circle-filled" : "-circle"

    return `${iconType}${iconSuffix}` as IconName
  }

  private getSrHint() {
    const layout = this.layout

    if (layout !== "notification" && layout !== "inline-notification") {
      return
    }

    switch (this.notificationType) {
      case "confirm":
      case "confirmation":
        return "This is a confirmation message."

      case "alert":
        return "This is an alert message."

      case "warning":
        return "This is a warning message."

      default:
        return "This is an information message."
    }
  }

  private isColorCard(): boolean {
    return (
      this.layout === "default" &&
      // Check explicitly because this.background can also be "dark" or even
      // the undocumented "gradient-1"
      (this.background === "blue" ||
        this.background === "grey" ||
        this.background === "orchid")
    )
  }

  private createIcon(srHint?: string) {
    const iconName = this.getIconName()

    if (!iconName) {
      return
    }

    return <sdx-icon class="icon" iconName={iconName} srHint={srHint} />
  }

  private getComponentClassNames() {
    return {
      component: true,
      animated: this.animated,
      "has-label": !!this.label,
      "has-description": !!this.getDescription(),
      "has-image": !!this.imageSrc,
      [getAppearance(this.el)]: true,
      [this.layout]: true,
      [this.notificationType]: true,
      [this.background || ""]: true,
      small: !!this.size,
    }
  }

  private getLabelClassNames() {
    const layout = this.layout

    return {
      label: true,
      [layout === "notification" ? "h5" : "h4"]: true,
    }
  }

  private getLinkClassNames() {
    const { target, href } = this

    return {
      [getIconLinkClassNames(href, target)]: true,
      "link--white":
        this.background === "dark" ||
        this.background === "orchid" ||
        this.background === "blue",
    }
  }

  public render() {
    const layout = this.layout
    const iconName = this.getIconName()
    const label = this.getLabel()

    const Tag =
      layout === "interaction" ||
      (this.isColorCard() && this.hasInteractionArea())
        ? "a"
        : "div"

    return (
      <Tag
        class={this.getComponentClassNames()}
        href={this.href}
        target={this.target}
      >
        <div class="first">
          {this.imageSrc && (
            <img
              class="image"
              src={this.imageSrc}
              alt={this.imageAlt}
              style={{ objectFit: this.objectFit }}
              loading={this.imageLoading}
              ref={(img) => (img!.fetchPriority = this.imageFetchPriority)}
            />
          )}

          {/* Allow the content notification-ish cards to "glide up" (when
            there's no label) by displaying the icon here and hiding it in the .label) */}
          {this.createIcon(this.getSrHint())}

          <slot name="first" />
        </div>

        <div class="second">
          {(label || iconName) && (
            <div
              class={this.getLabelClassNames()}
              role={this.labelAriaLevel ? "heading" : undefined}
              aria-level={this.labelAriaLevel}
            >
              {this.createIcon() ||
                (layout === "interaction" && this.size !== "small" && (
                  <div class="icon">&nbsp;</div>
                ))}
              <span class="title">{label}</span>
            </div>
          )}

          {hasSlot(this.el) && layout !== "interaction" && (
            <div class="slot">
              <slot />
            </div>
          )}

          {/* note: interaction area is not exclusive to layout=interaction */}
          {this.hasInteractionArea() && (
            <div class="flex-wrapper">
              <span class="description">{this.getDescription()}</span>
              <div class="interaction-area">
                {layout !== "interaction" && <slot name="interaction" />}

                {this.href && this.hrefLabel && (
                  <a
                    href={this.href}
                    class={this.getLinkClassNames()}
                    aria-label={this.hrefAriaLabel}
                    aria-hidden={layout === "interaction" ? "true" : undefined}
                    tabIndex={layout === "interaction" ? -1 : undefined}
                    target={this.target}
                    onClick={(e) => this.linkClickCallback(e)}
                  >
                    {layout !== "interaction" && this.hrefLabel}
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </Tag>
    )
  }
}
